import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import firebaseFunctionsService from '../../services/firebaseFunctionsService';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Box,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  User,
  DollarSign,
  Star,
  MessageSquare,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';
import ReviewModal from '../../components/reviews/ReviewModal';
import { hasExistingReview } from '../../services/reviewsService';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';

const PAGE_SIZE = 12;

const formatDate = date => {
  if (!date) return 'N/A';
  let dateObj;
  if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
  } else {
    return 'N/A';
  }

  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = time => {
  if (!time) return 'N/A';
  if (typeof time === 'string') {
    return time;
  }
  if (time instanceof Date) {
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return 'N/A';
};

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'delivered':
      return <Package className="h-4 w-4 text-purple-500" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-gray-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800';
    case 'delivered':
      return 'bg-purple-50 border-purple-200 text-purple-800';
    case 'cancelled':
      return 'bg-gray-50 border-gray-200 text-gray-800';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('myRequests.status.pending');
    case 'accepted':
      return t('myRequests.status.accepted');
    case 'rejected':
      return t('myRequests.status.rejected');
    case 'completed':
      return t('myRequests.status.completed');
    case 'delivered':
      return t('myRequests.status.delivered');
    case 'cancelled':
      return t('myRequests.status.cancelled');
    default:
      return status?.toUpperCase() || 'UNKNOWN';
  }
};

export default function MyRequests() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, initializing } = usePublicAuth();
  const dispatch = useAppDispatch();
  const [requests, setRequests] = useState([]);
  const [trips, setTrips] = useState({}); // { tripId: tripData }
  const [tripOwnerNames, setTripOwnerNames] = useState({}); // { tripId: ownerName }
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [reviewModal, setReviewModal] = useState({
    visible: false,
    reviewedUserId: null,
    reviewedUserName: '',
    requestId: null,
    role: 'traveler',
  });
  const [reviewStatuses, setReviewStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user?.uid && !user?.email) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        // Load user's requests (as requester)
        const requestsQueries = [];

        // Query by requesterId
        if (user?.uid) {
          requestsQueries.push(
            query(
              collection(db, 'requests'),
              where('requesterId', '==', user.uid),
            ),
          );
        }

        // Query by requesterEmail
        if (user?.email) {
          requestsQueries.push(
            query(
              collection(db, 'requests'),
              where('requesterEmail', '==', user.email),
            ),
          );
        }

        if (requestsQueries.length === 0) {
          setLoading(false);
          return;
        }

        const allRequestsSnapshots = await Promise.all(
          requestsQueries.map(q => getDocs(q)),
        );

        const requestMap = new Map();
        allRequestsSnapshots.forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            requestMap.set(doc.id, {
              id: doc.id,
              ...data,
            });
          });
        });
        const allRequests = Array.from(requestMap.values());

        // Filter only shipping trips (sender requests)
        const shippingRequests = [];
        const tripsData = {};

        // Load trips data for each request
        if (allRequests.length > 0) {
          const requestPromises = allRequests.map(async request => {
            if (!request.tripId) return null;

            try {
              // Check if trip already loaded
              if (!tripsData[request.tripId]) {
                const tripDoc = await getDoc(doc(db, 'trips', request.tripId));
                if (tripDoc.exists()) {
                  const tripData = { id: tripDoc.id, ...tripDoc.data() };
                  tripsData[request.tripId] = tripData;
                } else {
                  return null;
                }
              }

              const trip = tripsData[request.tripId];
              // Only include requests for shipping trips
              if (trip && trip.tripType === 'shipping') {
                return request;
              }
            } catch (error) {
              console.error('Error loading trip:', error);
            }
            return null;
          });

          const requestResults = await Promise.all(requestPromises);
          requestResults.forEach(result => {
            if (result) {
              shippingRequests.push(result);
            }
          });
        }

        // Load trip owner names
        const ownerNames = {};
        const ownerPromises = Object.values(tripsData).map(async trip => {
          if (!trip.createdBy && !trip.ownerId) {
            ownerNames[trip.id] = 'Traveler';
            return;
          }
          const ownerId = trip.createdBy || trip.ownerId;
          if (ownerNames[trip.id]) return;

          try {
            const ownerDoc = await getDoc(doc(db, 'users', ownerId));
            if (ownerDoc.exists()) {
              const ownerData = ownerDoc.data();
              ownerNames[trip.id] =
                ownerData.displayName ||
                `${ownerData.firstName || ''} ${
                  ownerData.lastName || ''
                }`.trim() ||
                ownerData.email?.split('@')[0] ||
                'Traveler';
            } else {
              ownerNames[trip.id] = 'Traveler';
            }
          } catch (error) {
            console.error('Error loading owner name:', error);
            ownerNames[trip.id] = 'Traveler';
          }
        });

        await Promise.all(ownerPromises);

        console.log('MyRequests - All requests:', allRequests.length);
        console.log('MyRequests - Shipping requests:', shippingRequests.length);
        console.log('MyRequests - Trips data:', Object.keys(tripsData).length);

        setRequests(shippingRequests);
        setTrips(tripsData);
        setTripOwnerNames(ownerNames);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up real-time listeners
    if (user?.uid || user?.email) {
      const unsubscribes = [];

      // Listen to requests by requesterId
      if (user?.uid) {
        const requestsQuery = query(
          collection(db, 'requests'),
          where('requesterId', '==', user.uid),
        );

        const unsubscribe = onSnapshot(requestsQuery, async snapshot => {
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Filter shipping trips
          const shippingRequests = [];
          const tripsData = { ...trips };
          const ownerNames = { ...tripOwnerNames };

          // Process each request
          const requestPromises = requestsData.map(async request => {
            if (!request.tripId) return null;

            try {
              // Load trip if not already loaded
              if (!tripsData[request.tripId]) {
                const tripDoc = await getDoc(doc(db, 'trips', request.tripId));
                if (tripDoc.exists()) {
                  const tripData = { id: tripDoc.id, ...tripDoc.data() };
                  tripsData[request.tripId] = tripData;

                  // Load owner name if not already loaded
                  if (
                    !ownerNames[request.tripId] &&
                    (tripData.createdBy || tripData.ownerId)
                  ) {
                    const ownerId = tripData.createdBy || tripData.ownerId;
                    try {
                      const ownerDoc = await getDoc(doc(db, 'users', ownerId));
                      if (ownerDoc.exists()) {
                        const ownerData = ownerDoc.data();
                        ownerNames[request.tripId] =
                          ownerData.displayName ||
                          `${ownerData.firstName || ''} ${
                            ownerData.lastName || ''
                          }`.trim() ||
                          ownerData.email?.split('@')[0] ||
                          'Traveler';
                      } else {
                        ownerNames[request.tripId] = 'Traveler';
                      }
                    } catch (error) {
                      console.error('Error loading owner name:', error);
                      ownerNames[request.tripId] = 'Traveler';
                    }
                  } else if (!ownerNames[request.tripId]) {
                    ownerNames[request.tripId] = 'Traveler';
                  }
                } else {
                  return null;
                }
              }

              const trip = tripsData[request.tripId];
              // Only include requests for shipping trips
              if (trip && trip.tripType === 'shipping') {
                return request;
              }
            } catch (error) {
              console.error('Error loading trip:', error);
            }
            return null;
          });

          const requestResults = await Promise.all(requestPromises);
          requestResults.forEach(result => {
            if (result) {
              shippingRequests.push(result);
            }
          });

          setRequests(shippingRequests);
          setTrips(tripsData);
          setTripOwnerNames(ownerNames);
        });

        unsubscribes.push(unsubscribe);
      }

      // Listen to requests by requesterEmail
      if (user?.email) {
        const requestsQuery = query(
          collection(db, 'requests'),
          where('requesterEmail', '==', user.email),
        );

        const unsubscribe = onSnapshot(requestsQuery, async snapshot => {
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Filter shipping trips
          const shippingRequests = [];
          const tripsData = { ...trips };
          const ownerNames = { ...tripOwnerNames };

          // Process each request
          const requestPromises = requestsData.map(async request => {
            if (!request.tripId) return null;

            try {
              // Load trip if not already loaded
              if (!tripsData[request.tripId]) {
                const tripDoc = await getDoc(doc(db, 'trips', request.tripId));
                if (tripDoc.exists()) {
                  const tripData = { id: tripDoc.id, ...tripDoc.data() };
                  tripsData[request.tripId] = tripData;

                  // Load owner name if not already loaded
                  if (
                    !ownerNames[request.tripId] &&
                    (tripData.createdBy || tripData.ownerId)
                  ) {
                    const ownerId = tripData.createdBy || tripData.ownerId;
                    try {
                      const ownerDoc = await getDoc(doc(db, 'users', ownerId));
                      if (ownerDoc.exists()) {
                        const ownerData = ownerDoc.data();
                        ownerNames[request.tripId] =
                          ownerData.displayName ||
                          `${ownerData.firstName || ''} ${
                            ownerData.lastName || ''
                          }`.trim() ||
                          ownerData.email?.split('@')[0] ||
                          'Traveler';
                      } else {
                        ownerNames[request.tripId] = 'Traveler';
                      }
                    } catch (error) {
                      console.error('Error loading owner name:', error);
                      ownerNames[request.tripId] = 'Traveler';
                    }
                  } else if (!ownerNames[request.tripId]) {
                    ownerNames[request.tripId] = 'Traveler';
                  }
                } else {
                  return null;
                }
              }

              const trip = tripsData[request.tripId];
              // Only include requests for shipping trips
              if (trip && trip.tripType === 'shipping') {
                return request;
              }
            } catch (error) {
              console.error('Error loading trip:', error);
            }
            return null;
          });

          const requestResults = await Promise.all(requestPromises);
          requestResults.forEach(result => {
            if (result) {
              shippingRequests.push(result);
            }
          });

          setRequests(prev => {
            // Merge with existing requests, avoiding duplicates and updating existing ones
            const requestMap = new Map(prev.map(r => [r.id, r]));
            shippingRequests.forEach(r => {
              requestMap.set(r.id, r);
            });
            return Array.from(requestMap.values());
          });
          setTrips(tripsData);
          setTripOwnerNames(ownerNames);
        });

        unsubscribes.push(unsubscribe);
      }

      return () => {
        unsubscribes.forEach(unsub => unsub());
      };
    }
  }, [user?.uid, user?.email]);

  const handleCancelRequest = async requestId => {
    if (processingRequest) return;
    if (!confirm(t('myRequests.areYouSureCancel'))) {
      return;
    }
    setProcessingRequest(requestId);
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: 'cancelled',
        updatedAt: new Date(),
      });
      alert(t('myRequests.requestCancelledSuccessfully'));
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert(t('myRequests.failedToCancelRequest'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleOpenPaymentModal = request => {
    const travelerInfo = getTripOwnerInfo(request);
    const rawPrice = request?.price ?? request?.estimatedPrice ?? 0;
    const currency = request?.currency || 'USD';
    const costNum =
      typeof rawPrice === 'string'
        ? parseFloat(String(rawPrice).replace(/[^0-9.]/g, ''))
        : Number(rawPrice);
    const fee = !isNaN(costNum) ? costNum * 0.15 : 0;
    const total = !isNaN(costNum) ? costNum + fee : 0;
    setPaymentModalData({
      requestId: request.id,
      currency,
      subtotal: costNum,
      fee,
      total,
      travelerName: travelerInfo.name,
    });
    setTipAmount('');
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!paymentModalData || !user?.uid) return;

    if (confirmingPayment) return;

    try {
      setConfirmingPayment(true);
      setProcessingRequest(paymentModalData.requestId);

      const requestDoc = await getDoc(
        doc(db, 'requests', paymentModalData.requestId),
      );
      if (!requestDoc.exists()) {
        throw new Error('Request not found');
      }

      const requestData = requestDoc.data();
      const trip = trips[requestData.tripId];

      const payerId = requestData.requesterId || user.uid;
      const receiverId =
        requestData.tripOwnerId ||
        trip?.createdBy ||
        trip?.ownerId ||
        trip?.userId;

      if (!payerId || !receiverId) {
        throw new Error('Missing payer or receiver information');
      }

      const authToken = await auth.currentUser?.getIdToken();
      if (!authToken) {
        throw new Error('User not authenticated');
      }

      const tipAmountValue = parseFloat(tipAmount || 0);
      await firebaseFunctionsService.confirmDeliveryPayment(
        paymentModalData.requestId,
        payerId,
        receiverId,
        paymentModalData.subtotal,
        paymentModalData.fee,
        paymentModalData.total,
        paymentModalData.currency,
        requestData.itemTitle || requestData.itemDescription || 'Item',
        authToken,
        tipAmountValue,
      );

      setShowPaymentModal(false);
      setPaymentModalData(null);
      setTipAmount('');
      alert(t('myRequests.paymentConfirmed'));
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert(error.message || t('myRequests.failedToConfirmPayment'));
    } finally {
      setConfirmingPayment(false);
      setProcessingRequest(null);
    }
  };

  const getTripOwnerInfo = request => {
    const trip = trips[request.tripId];
    return {
      id:
        request.tripOwnerId || trip?.createdBy || trip?.ownerId || trip?.userId,
      name:
        tripOwnerNames[request.tripId] ||
        request.tripOwnerName ||
        trip?.ownerName ||
        trip?.createdByName ||
        trip?.createdByEmail?.split('@')[0] ||
        'Traveler',
    };
  };

  const handleOpenReviewModal = async request => {
    if (!user?.uid) {
      alert('Please log in to rate other users.');
      return;
    }
    const ownerInfo = getTripOwnerInfo(request);
    if (!ownerInfo.id) {
      alert('Trip owner information is missing.');
      return;
    }
    try {
      const alreadyReviewed = await hasExistingReview(
        ownerInfo.id,
        request.id,
        user.uid,
      );
      if (alreadyReviewed) {
        setReviewStatuses(prev => ({ ...prev, [request.id]: true }));
        alert('You have already reviewed this user.');
        return;
      }
      setReviewModal({
        visible: true,
        reviewedUserId: ownerInfo.id,
        reviewedUserName: ownerInfo.name,
        requestId: request.id,
        role: 'traveler',
      });
    } catch (error) {
      alert(error.message || 'Failed to open review modal. Please try again.');
    }
  };

  const handleReviewSuccess = requestId => {
    setReviewStatuses(prev => ({ ...prev, [requestId]: true }));
  };

  const handleStartChat = async (targetUserId, targetUserName) => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!targetUserId) {
      navigate('/chat');
      return;
    }

    try {
      const conversation = await dispatch(
        openOrCreateConversationByUserId(
          targetUserId,
          targetUserName || 'User',
        ),
      );
      if (conversation?.id) {
        navigate(`/chat/${conversation.id}`);
      } else {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      navigate('/chat');
    }
  };

  useEffect(() => {
    if (!user?.uid || requests.length === 0) {
      setReviewStatuses({});
      return;
    }

    let isMounted = true;

    const fetchStatuses = async () => {
      try {
        const entries = await Promise.all(
          requests.map(async request => {
            const ownerInfo = getTripOwnerInfo(request);
            if (!ownerInfo.id) {
              return [request.id, false];
            }
            try {
              const exists = await hasExistingReview(
                ownerInfo.id,
                request.id,
                user.uid,
              );
              return [request.id, exists];
            } catch (error) {
              console.error('Error checking review status:', error);
              return [request.id, false];
            }
          }),
        );

        if (!isMounted) return;
        const statusMap = {};
        entries.forEach(([id, status]) => {
          if (id) {
            statusMap[id] = status;
          }
        });
        setReviewStatuses(statusMap);
      } catch (error) {
        console.error('Error loading review statuses:', error);
      }
    };

    fetchStatuses();

    return () => {
      isMounted = false;
    };
  }, [requests, user?.uid, trips, tripOwnerNames]);

  // Pagination effect
  useEffect(() => {
    const sortedRequests = [...requests].sort((a, b) => {
      const aTime = a.createdAt?.toDate
        ? a.createdAt.toDate().getTime()
        : a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0;
      const bTime = b.createdAt?.toDate
        ? b.createdAt.toDate().getTime()
        : b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0;
      return bTime - aTime;
    });
    const totalPages = Math.max(
      1,
      Math.ceil(sortedRequests.length / PAGE_SIZE),
    );
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [requests, currentPage]);

  const renderRequestCard = request => {
    const trip = trips[request.tripId];
    if (!trip) return null;
    const ownerInfo = getTripOwnerInfo(request);

    const canCancel =
      request.status === 'pending' || request.status === 'accepted';
    const canConfirmPayment = request.status === 'delivered';

    return (
      <div className="group relative rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700 transition-all duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 dark:text-white truncate">
                  {tripOwnerNames[request.tripId] ||
                    request.tripOwnerName ||
                    'Traveler'}
                </h3>
                <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                  {t('myRequests.tripOwner')}
                </p>
              </div>
            </div>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor(
              request.status,
            )} flex-shrink-0`}
          >
            {getStatusIcon(request.status)}
            <span className="text-xs font-bold">
              {getStatusLabel(request.status, t)}
            </span>
          </div>
        </div>

        {/* Item Info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-4 w-4 text-purple-500 dark:text-purple-400" />
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              {t('myRequests.item')}
            </p>
          </div>
          <p className="text-base font-bold text-gray-900 dark:text-white">
            {request.itemTitle || request.itemDescription || 'N/A'}
          </p>
        </div>

        {/* Route Info */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              {t('myRequests.route')}
            </p>
          </div>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {trip.originDetails?.city || trip.originCity || 'Origin'},{' '}
            {trip.originDetails?.country || trip.originCountry || ''} →{' '}
            {trip.destinationDetails?.city ||
              trip.destinationCity ||
              'Destination'}
            ,{' '}
            {trip.destinationDetails?.country || trip.destinationCountry || ''}
          </p>
        </div>

        {/* Trip Details */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            <p className="text-xs text-gray-600 dark:text-slate-400">
              {t('myRequests.departure')}: {formatDate(trip.departureDate)} at{' '}
              {formatTime(trip.departureTime)}
            </p>
          </div>

          {request.availableSpace && (
            <div className="flex items-center gap-2">
              <Box className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              <p className="text-xs text-gray-600 dark:text-slate-400">
                {t('myRequests.space')}: {request.availableSpace}{' '}
                {request.spaceUnit || trip.spaceUnit || 'kg'}
              </p>
            </div>
          )}

          {(request.price || request.estimatedPrice) && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
              <p className="text-xs font-bold text-gray-900 dark:text-white">
                {t('myRequests.price')}: {request.currency || 'USD'}{' '}
                {request.price || request.estimatedPrice || 'N/A'}
              </p>
            </div>
          )}
        </div>

        {/* Notes */}
        {request.additionalNotes && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
              {t('myRequests.notes')}
            </p>
            <p className="text-sm text-gray-700 dark:text-slate-300 line-clamp-2">
              {request.additionalNotes}
            </p>
          </div>
        )}

        {/* Waiting message for accepted status */}
        {request.status === 'accepted' && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                {t('myRequests.waitingForDelivery', { name: ownerInfo.name })}
                {ownerInfo.email && (
                  <span className="text-xs text-gray-500 dark:text-slate-500">
                    ({ownerInfo.email})
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-slate-700 flex-wrap">
          {canConfirmPayment && (
            <button
              onClick={() => handleOpenPaymentModal(request)}
              disabled={processingRequest === request.id}
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="h-5 w-5" />
              {t('myRequests.confirmPayment')}
            </button>
          )}

          {(request.status === 'delivered' ||
            request.status === 'completed') && (
            <button
              onClick={() => {
                if (!reviewStatuses[request.id]) {
                  handleOpenReviewModal(request);
                }
              }}
              disabled={reviewStatuses[request.id]}
              className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm ${
                reviewStatuses[request.id]
                  ? 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-slate-400 cursor-not-allowed'
                  : 'bg-yellow-500 text-black hover:bg-yellow-400'
              }`}
            >
              <Star className="h-4 w-4" />
              {reviewStatuses[request.id] ? t('myRequests.reviewed') : t('myRequests.rate')}
            </button>
          )}

          {canCancel && (
            <button
              onClick={() => handleCancelRequest(request.id)}
              disabled={processingRequest === request.id}
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="h-5 w-5" />
              {t('myRequests.cancelRequest')}
            </button>
          )}

          <button
            onClick={() => {
              handleStartChat(ownerInfo.id, ownerInfo.name);
            }}
            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
          >
            <MessageCircle className="h-5 w-5" />
            {t('myRequests.message')}
          </button>
          <button
            onClick={() => navigate('/profile/support')}
            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
          >
            <MessageSquare className="h-5 w-5" />
            {t('profile.supportTitle') || 'Support'}
          </button>
        </div>
      </div>
    );
  };

  const isLoading = initializing || loading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero Section Skeleton */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg w-4/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-96 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          {/* Cards Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-96 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-5/6 mb-4 animate-pulse"></div>
                <div className="h-20 bg-gray-200 dark:bg-slate-800 rounded-xl mb-4 animate-pulse"></div>
                <div className="flex gap-3 mb-4">
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                </div>
                <div className="h-12 bg-gray-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('myRequests.pleaseLogInToView')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('incomingRequests.goToLogin')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('myRequests.myRequests')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('myRequests.viewAndManageRequests')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        {requests.length === 0 ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <Box className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('myRequests.noRequestsYet')}
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('myRequests.haventSentAnyRequests')}
            </p>
            <a
              href="/request-delivery"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-6 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('incomingRequests.createRequest')}
            </a>
          </div>
        ) : (
          <>
            {(() => {
              const sortedRequests = [...requests].sort((a, b) => {
                const aTime = a.createdAt?.toDate
                  ? a.createdAt.toDate().getTime()
                  : a.createdAt
                  ? new Date(a.createdAt).getTime()
                  : 0;
                const bTime = b.createdAt?.toDate
                  ? b.createdAt.toDate().getTime()
                  : b.createdAt
                  ? new Date(b.createdAt).getTime()
                  : 0;
                return bTime - aTime;
              });
              const totalPages = Math.max(
                1,
                Math.ceil(sortedRequests.length / PAGE_SIZE),
              );
              const startIndex = (currentPage - 1) * PAGE_SIZE;
              const endIndex = startIndex + PAGE_SIZE;
              const paginatedRequests = sortedRequests.slice(
                startIndex,
                endIndex,
              );

              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedRequests.map(request => (
                      <div key={request.id}>{renderRequestCard(request)}</div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-center gap-2">
                      <button
                        onClick={() =>
                          setCurrentPage(prev => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                        className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                      >
                        {t('myRequests.prev')}
                      </button>
                      {[...Array(totalPages)].map((_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => setCurrentPage(index + 1)}
                          className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                            currentPage === index + 1
                              ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                          }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      <button
                        onClick={() =>
                          setCurrentPage(prev => Math.min(totalPages, prev + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                      >
                        {t('myRequests.next')}
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && paymentModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800">
            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4 text-center">
              {t('myRequests.confirmPaymentTitle')}
            </h3>
            <p className="text-center text-sm text-gray-600 dark:text-slate-400 mb-6">
              {t('myRequests.hasReportedDelivered', { name: paymentModalData.travelerName || t('myRequests.thisTraveler') })}
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-slate-400">
                  {t('myRequests.subtotal')}
                </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {paymentModalData.subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-slate-400">
                  {t('myRequests.fees15')}
                </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency} {paymentModalData.fee.toFixed(2)}
                </span>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  {t('flexyRideMyRequests.modalTipLabel')}
                </label>
                <input
                  type="text"
                  placeholder={t('flexyRideMyRequests.modalTipPlaceholder')}
                  value={tipAmount}
                  onChange={e => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = cleaned.split('.');
                    const filtered =
                      parts.length > 2
                        ? parts[0] + '.' + parts.slice(1).join('')
                        : cleaned;
                    setTipAmount(filtered);
                  }}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              {tipAmount && parseFloat(tipAmount) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    {t('flexyRideMyRequests.modalTip')}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {paymentModalData.currency}{' '}
                    {parseFloat(tipAmount || 0).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center py-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('myRequests.total')}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {(
                    paymentModalData.total + parseFloat(tipAmount || 0)
                  ).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setTipAmount('');
                  setPaymentModalData(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                {t('myRequests.no')}
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={confirmingPayment || processingRequest === paymentModalData.requestId}
                className="flex-1 px-4 py-3 bg-black dark:bg-yellow-500 text-yellow-500 dark:text-black rounded-xl font-bold hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
              >
                {confirmingPayment
                  ? t('flexyRideMyRequests.modalProcessing')
                  : t('myRequests.yes')}
              </button>
            </div>
          </div>
        </div>
      )}
      <ReviewModal
        visible={reviewModal.visible}
        reviewedUserId={reviewModal.reviewedUserId}
        reviewedUserName={reviewModal.reviewedUserName}
        requestId={reviewModal.requestId}
        userRole={reviewModal.role}
        onClose={() =>
          setReviewModal(prev => ({
            ...prev,
            visible: false,
          }))
        }
        onSubmitSuccess={() => {
          if (reviewModal.requestId) {
            handleReviewSuccess(reviewModal.requestId);
          }
        }}
      />
    </div>
  );
}
